import { ref, watch } from "vue";
import StorageService from "@/services/storage.service";

export type ThemeMode = "light" | "dark" | "auto";

const STORAGE_KEY = "theme-preference";
const currentTheme = ref<ThemeMode>("auto");
const effectiveTheme = ref<"light" | "dark">("light");

let mediaQueryListener: ((e: MediaQueryListEvent) => void) | null = null;

const getSystemPreference = (): "light" | "dark" => {
    return window.matchMedia("(prefers-color-scheme: dark)").matches
        ? "dark"
        : "light";
};

const applyTheme = (theme: "light" | "dark") => {
    effectiveTheme.value = theme;
    const root = document.documentElement;

    if (theme === "dark") {
        root.classList.add("dark");
    } else {
        root.classList.remove("dark");
    }
};

const updateEffectiveTheme = (mode: ThemeMode) => {
    if (mode === "auto") {
        applyTheme(getSystemPreference());
    } else {
        applyTheme(mode);
    }
};

const setupMediaQueryListener = () => {
    // Remove existing listener if any
    if (mediaQueryListener) {
        window
            .matchMedia("(prefers-color-scheme: dark)")
            .removeEventListener("change", mediaQueryListener);
    }

    // Only add listener if current mode is auto
    if (currentTheme.value === "auto") {
        mediaQueryListener = (e: MediaQueryListEvent) => {
            if (currentTheme.value === "auto") {
                applyTheme(e.matches ? "dark" : "light");
            }
        };

        window
            .matchMedia("(prefers-color-scheme: dark)")
            .addEventListener("change", mediaQueryListener);
    }
};

export default function useTheme() {
    const initializeTheme = async () => {
        // Load saved preference or default to auto
        const savedTheme =
            ((await StorageService.get(STORAGE_KEY)) as ThemeMode) || "auto";
        currentTheme.value = savedTheme;

        // Apply the theme
        updateEffectiveTheme(savedTheme);

        // Setup listener for system preference changes
        setupMediaQueryListener();
    };

    const setTheme = async (mode: ThemeMode) => {
        currentTheme.value = mode;

        // Save to storage
        await StorageService.set(STORAGE_KEY, mode);

        // Apply the theme
        updateEffectiveTheme(mode);

        // Setup/remove media query listener based on mode
        setupMediaQueryListener();
    };

    // Watch for theme changes
    watch(currentTheme, (newMode) => {
        updateEffectiveTheme(newMode);
    });

    return {
        currentTheme,
        effectiveTheme,
        initializeTheme,
        setTheme,
    };
}
